import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Form,
  Input,
  Upload,
  Button,
  Image,
  notification,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useMediaQuery } from "react-responsive";
import config from "../../../../config/config";
import urldata from "../../../../urldata";

const BecomeOneOfUs = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [webFileList, setWebFileList] = useState([]);
  const [mobileFileList, setMobileFileList] = useState([]);

  const [form] = Form.useForm();
  const base_api = urldata + "admin/";
  const getEndpoint = base_api + "getBecomeOneOfUs";
  const addEndpoint = base_api + "addBecomeOneOfUs";

  const azure_base = config.azure_object_url || "";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(getEndpoint);
      const section = response?.data?.data?.[0] || null;
      setData(section);

      if (section) {
        form.setFieldsValue({
          title: section.title,
          subtitle: section.subtitle,
          url: section.url,
        });

        // Prefill Upload fileLists with secure URLs
        setWebFileList(
          section.webImage
            ? [
                {
                  uid: "-1",
                  name: "webImg.png",
                  status: "done",
                  url: `${azure_base}${section.webImage}`,
                },
              ]
            : []
        );
        setMobileFileList(
          section.mobileImage
            ? [
                {
                  uid: "-2",
                  name: "mobileImg.png",
                  status: "done",
                  url: `${azure_base}${section.mobileImage}`,
                },
              ]
            : []
        );
      }
    } catch (err) {
      console.error(err);
      notification.error({ message: "Failed to fetch section" });
    }
  };

  const handleSubmit = async (values) => {
    if (!webFileList[0] || !mobileFileList[0]) {
      notification.error({
        message: "Both Web and Mobile images are required",
      });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("subtitle", values.subtitle);
    formData.append("url", values.url);

    // Only append new files if uploaded
    if (webFileList[0].originFileObj)
      formData.append("webImg", webFileList[0].originFileObj);
    if (mobileFileList[0].originFileObj)
      formData.append("mobileImg", mobileFileList[0].originFileObj);

    try {
      const response = await axios.post(addEndpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notification.success({
        message: response?.data?.message || "Saved successfully",
      });
      fetchData(); // Refresh data
    } catch (err) {
      console.error(err);
      notification.error({
        message: err?.response?.data?.message || "Failed to save section",
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    beforeUpload: () => false,
    maxCount: 1,
    listType: "picture-card",
  };

  return (
    <div>
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Become One of Us</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        <Col span={24}>
          <Card title="Become One of Us" bordered={false}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                label="Title"
                name="title"
                rules={[
                  { required: true, message: "Title is required" },
                  { max: 50 },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Subtitle"
                name="subtitle"
                rules={[
                  { required: true, message: "Subtitle is required" },
                  { max: 200 },
                ]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>

              <Form.Item
                label="URL"
                name="url"
                extra="i.e. /collaborate"
                rules={[{ required: true, message: "URL is required" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item label="Desktop Image" extra="Recommended size: 1920 × 719">
                {/* Show current image (existing or newly selected) */}
                {webFileList.length > 0 && (
                  <Image
                    src={
                      webFileList[0].url ||
                      webFileList[0].thumbUrl ||
                      (webFileList[0].originFileObj &&
                        URL.createObjectURL(webFileList[0].originFileObj))
                    }
                    width={isMobile ? 150 : 300}
                    style={{
                      marginBottom: 10,
                      display: "block",
                      objectFit: "cover",
                    }}
                    alt="Web preview"
                  />
                )}

                <Upload
                  {...uploadProps}
                  accept=".jpg,.jpeg,.png,.svg"
                  fileList={webFileList} // ← Important: show current file in upload component
                  onRemove={() => setWebFileList([])} // Allow removing
                  onChange={({ fileList }) => {
                    // Keep only the latest file (since maxCount: 1)
                    const latestFile = fileList.slice(-1);
                    setWebFileList(latestFile);
                  }}
                  beforeUpload={(file) => {
                    const ext = file.name.split(".").pop().toLowerCase();
                    if (!["jpg", "jpeg", "png", "svg"].includes(ext)) {
                      notification.error({
                        message: "Invalid file format",
                        description: "Only JPG, JPEG, PNG, SVG allowed",
                      });
                      return Upload.LIST_IGNORE;
                    }
                    return false; // Prevent auto upload
                  }}
                >
                  {webFileList.length === 0 && (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              <Form.Item
                label="Mobile Image"
                extra="Recommended size: 750 × 1114"
              >
                {mobileFileList.length > 0 && (
                  <Image
                    src={
                      mobileFileList[0].url ||
                      mobileFileList[0].thumbUrl ||
                      (mobileFileList[0].originFileObj &&
                        URL.createObjectURL(mobileFileList[0].originFileObj))
                    }
                    width={isMobile ? 150 : 300}
                    style={{
                      marginBottom: 10,
                      display: "block",
                      objectFit: "cover",
                    }}
                    alt="Mobile preview"
                  />
                )}

                <Upload
                  {...uploadProps}
                  accept=".jpg,.jpeg,.png,.svg"
                  fileList={mobileFileList}
                  onRemove={() => setMobileFileList([])}
                  onChange={({ fileList }) => {
                    const latestFile = fileList.slice(-1);
                    setMobileFileList(latestFile);
                  }}
                  beforeUpload={(file) => {
                    const ext = file.name.split(".").pop().toLowerCase();
                    if (!["jpg", "jpeg", "png", "svg"].includes(ext)) {
                      notification.error({
                        message: "Invalid file format",
                        description: "Only JPG, JPEG, PNG, SVG allowed",
                      });
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                >
                  {mobileFileList.length === 0 && (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Save
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BecomeOneOfUs;
